import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Edit3, Rocket } from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Batch 1C-A FINAL §9 — post-initial-setup Kickback Review page.
 *
 * Once initial setup is completed, "Review & Save" should not force the
 * Creator back through Step 5 of the creation wizard. This page shows
 * the same section summaries used at Step 5, plus links to Edit
 * Kickback / Launch Readiness.
 */
const KickbackReviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [draft, setDraft] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem('token');
      const H = { Authorization: `Bearer ${token}` };
      try {
        const p = await axios.get(`${API}/creator/portfolios/${id}`, { headers: H });
        setPortfolio(p.data.portfolio);
        if (p.data.portfolio?.contract_id) {
          try {
            const d = await axios.get(`${API}/contracts/drafts/${p.data.portfolio.contract_id}`, { headers: H });
            setDraft(d.data.draft || d.data);
          } catch (_) { /* */ }
        }
        const r = await axios.get(`${API}/creator/portfolios/${id}/readiness`, { headers: H });
        setReadiness(r.data.readiness);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <div style={{ padding: 60, textAlign: 'center' }}>Loading…</div>;
  if (!portfolio) return <div style={{ padding: 60, textAlign: 'center' }}>Kickback not found.</div>;

  const ev = portfolio.offer_evidence || {};
  const evImgN = (ev.evidence_images || ev.images || []).length + (ev.product_images || []).length + (ev.proof_images || []).length + (ev.design_images || []).length;
  const evDocN = (ev.supporting_documents || ev.documents || []).length + (ev.screenshots || []).length;
  const p = draft?.params || {};
  const rows = [
    ['Kickback Overview', [
      ['Name', portfolio.name],
      ['Description', portfolio.description],
      ['Category', portfolio.category],
      ['Kickback Types', (portfolio.portfolio_type_ids || []).join(', ') || portfolio.portfolio_type],
      ['Quantity', portfolio.quantity],
      ['Unit Price', portfolio.unit_price],
    ]],
    ['Item / Offer Evidence', [
      ['Offer Type', ev.offer_type || ev.type],
      ['Product / Offer Images', (ev.product_images || []).length + (ev.evidence_images || ev.images || []).length],
      ['Proof / Marking Images', (ev.proof_images || []).length],
      ['Design / Diagram Images', (ev.design_images || []).length],
      ['Supporting Documents', (ev.supporting_documents || ev.documents || []).length + (ev.screenshots || []).length],
      ['Total Evidence Assets', evImgN + evDocN],
      ['Status', (evImgN + evDocN > 0 || ev.offer_type) ? 'Complete' : 'Incomplete'],
    ]],
    ['Kickback Artwork', [
      ['Selected artwork count', (portfolio.nft_asset_ids || []).length],
    ]],
    ['Contract Draft', draft ? [
      ['Contract Standard', draft.standard || draft.template_id],
      ['Token Name', p.name],
      ['Token Symbol', p.symbol],
      ['Maximum Supply', p.max_supply],
      ['Mint Price', p.mint_price],
      ['Royalty', p.royalty_percent],
      ['Base URI / Metadata', p.base_uri],
      ['Draft Status', draft.deployed ? 'Deployed' : 'Created'],
    ] : [['Contract Draft', 'None']]],
    ['Management & Terms', [
      ['Management Type', portfolio.management_type],
      ['Kickback %', portfolio.kickback_percent],
      ['Quantity', portfolio.quantity],
      ['Unit Price', portfolio.unit_price],
    ]],
  ];

  return (
    <div data-testid="kickback-review-page" style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px' }}>
      <button onClick={() => navigate(`/creator/portfolios/${id}`)}
        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.primary, fontSize: 14, fontWeight: 600, marginBottom: 20 }}>
        <ArrowLeft size={16} /> Back to Kickback
      </button>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: theme.colors.text.primary, marginBottom: 6 }}>Kickback Review</h1>
      <p style={{ fontSize: 13, color: theme.colors.text.secondary, marginBottom: 24 }}>
        Review the current setup, confirm key information, and identify anything that still needs attention before launch.
      </p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        <button data-testid="review-edit-btn" onClick={() => navigate(`/creator/portfolios/${id}/edit`)}
          style={{ padding: '8px 14px', border: 'none', background: theme.colors.primary, color: '#FFF', borderRadius: theme.radius.sm, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Edit3 size={14} /> Edit Kickback
        </button>
        <button data-testid="review-launch-btn" onClick={() => navigate(`/creator/portfolios/${id}/launch-readiness`)}
          style={{ padding: '8px 14px', border: `1px solid ${theme.colors.borders}`, background: '#FFF', color: theme.colors.text.primary, borderRadius: theme.radius.sm, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Rocket size={14} /> Review Launch Readiness
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {rows.map(([label, kv]) => (
          <div key={label} data-testid={`review-section-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
            style={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.md, padding: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text.primary, marginBottom: 10 }}>{label}</h3>
            <dl style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '6px 16px', fontSize: 13, margin: 0 }}>
              {kv.map(([k, v]) => (
                <React.Fragment key={k}>
                  <dt style={{ color: theme.colors.text.muted, fontWeight: 500 }}>{k}</dt>
                  <dd style={{ color: theme.colors.text.primary, margin: 0, wordBreak: 'break-word' }}>{v ?? '—'}</dd>
                </React.Fragment>
              ))}
            </dl>
          </div>
        ))}
      </div>
      {readiness && (
        <p style={{ marginTop: 20, fontSize: 12, color: theme.colors.text.muted }}>
          Remaining launch requirements: {readiness.completed_required_count} / {readiness.required_count} complete ({readiness.percent}%).
        </p>
      )}
    </div>
  );
};

export default KickbackReviewPage;
